


































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  SingleSitePayload,
  SingleSiteSamplingRequestPayload,
  AuditSamplingResponse,
  AuditSamplingDataConfig,
  AuditCriteriaMapStatus,
  CalculationConfig,
} from '@/store/modules/audits/types/audits.types';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
  find as _find,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import SamplingCalculator from '@/views/Audit/components/SamplingCalculator.vue';
import OneOffSamplingCalculator from '@/views/Audit/components/OneOffSamplingCalculator.vue';
import { AuditSamplingType } from '@/jbi-shared/types/project.types';

@Component({
  components: {
    OneOffSamplingCalculator,
    SamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class TargetSampleSizeSingleSiteSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSamplingType!: AuditSamplingType;
  @Prop()
  public auditCategory!: string;
  @Prop({ default: true })
  public editable!: boolean;

  public singleSitePayload: SingleSitePayload[] = [];
  public calculateData: any;
  public oneOffCalculateData = {
    confidenceLevel: 95,
    population: 100,
    margin: 5,
    groupOne: 80,
    recommended: 0,
    output: 0,
  };

  @Action('audits/updateSingleSiteSampling')
  public updateSingleSiteSampling!: (
    payload: SingleSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedSingleSiteSampling)
  public updatedSingleSiteSampling!: any;

  @Watch('updatedSingleSiteSampling')
  @isTruthy
  public watchUpdatedSingleSiteSampling() {
    this.showSaveSuccessNotification();
  }

  public mounted() {
    this.calculateData = this.findCalculationConfig(null);
    this.populateSingleSiteData();
  }

  /**
   * Populates the singleSitePayload array with SingleSitePayload objects.
   * For single-site sampling, this method iterates over each group of data configs
   * and processes only active criteria that are for a specific site.
   * The data config will have a non-null `auditSiteMap` in this case.
   * It then creates a SingleSitePayload for each active criteria/site combination
   * and initializes it with the criteria id, audit criteria id, criteria title,
   * order, sample size, and calculation data.
   */
  public populateSingleSiteData() {
    const payloads: SingleSitePayload[] = [];

    // Iterate over each group of data configs
    _map(
      this.auditConfig.dataConfigs,
      (dataConfigGroup: AuditSamplingDataConfig[]) => {
        // Iterate over each config within the group
        _map(dataConfigGroup, (config: AuditSamplingDataConfig) => {
          // For single-site sampling, process only active criteria that are for a specific site.
          // The data config will have a non-null `auditSiteMap` in this case.
          if (
            config.auditCriteriaMap.status === AuditCriteriaMapStatus.ACTIVE &&
            config.auditSiteMap
          ) {
            const payload: SingleSitePayload = {
              auditId: this.auditId,
              criteriaId: config.auditCriteriaMap.criteria.id,
              auditCriteriaId: config.auditCriteriaMap.id,
              criteriaTitle: config.auditCriteriaMap.criteria.title,
              order: config.auditCriteriaMap.order,
              sampleSize: 0, // Initialize with a default
              calculateData: this.findCalculationConfig(
                config.auditCriteriaMap.id,
              ),
              dataConfig: config, // Keep a reference to the original config if needed
            };

            payload.sampleSize = _round(config.sampleSize);
            payloads.push(payload);
          }
        });
      },
    );

    this.singleSitePayload = payloads;
  }

  /**
   * Handles user input for the sample size text field.
   * If the length of the input is greater than the max length, it will be truncated.
   * The input value is then cast to a number.
   * @param newValue The new value entered by the user.
   * @param row The row of the table that the input is for.
   */
  public handleSampleSizeInput(newValue: string, row: SingleSitePayload) {
    const maxLength = 7;
    let valueToUpdate = newValue;

    if (valueToUpdate && valueToUpdate.length > maxLength) {
      valueToUpdate = valueToUpdate.slice(0, maxLength);
    }

    // The 'input' event for type="number" can return an empty string.
    // We'll cast it to a number, which will correctly result in 0 for an empty string.
    row.sampleSize = Number(valueToUpdate);
  }

  public handleNumericInput(event: KeyboardEvent) {
    // Allowed keys: Backspace, Delete, Tab, and arrow keys for navigation
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow Copy/Paste/Select All keyboard shortcuts
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // Prevent the event if the key is not a digit
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  public handleSingleSiteChanges() {
    this.updateSingleSiteSampling({
      auditId: this.auditId,
      singleSitePayload: this.singleSitePayload,
      auditSamplingType: this.auditSamplingType,
      calculateData: this.calculateData,
      samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
    });
  }

  /**
   * Handles applying a sample size to either a specific criterion row
   * or to all criteria via the header calculator.
   * @param calculateData - The data to update, either for a single
   *   criterion or for all criteria.
   */
  public handleApplySampleSize(calculateData: any) {
    // This branch handles applying a sample size to a specific criterion row.
    if (calculateData.auditCriteriaId !== undefined) {
      // Find the correct payload in the original data source (`singleSitePayload`),
      // not the sorted computed property (`auditCriteriaList`).
      const payloadToUpdate = _find(this.singleSitePayload, {
        auditCriteriaId: calculateData.auditCriteriaId,
      });

      // Check if the payload was found before attempting to update it.
      if (payloadToUpdate) {
        payloadToUpdate.sampleSize = calculateData.output;
        // Also update the calculation data for this specific row
        payloadToUpdate.calculateData = calculateData;
      }
    } else {
      // This branch handles applying a sample size to ALL criteria from the header calculator.
      this.calculateData = calculateData;
      // Iterate over the original data source to update all items.
      this.singleSitePayload.forEach((payload) => {
        payload.sampleSize = calculateData.output;
        // Also update the calculation data for each individual criterion,
        // preserving its unique auditCriteriaId.
        payload.calculateData = {
          ...calculateData,
          auditCriteriaId: payload.auditCriteriaId,
        };
      });
    }
  }

  /**
   * Finds the calculation configuration for the given audit criteria ID
   * and audit site ID. The configuration is determined in the following
   * order of precedence:
   *
   * 1. A configuration with a matching criteria ID and no site ID.
   * 2. The global configuration, with neither a criteria ID nor site ID.
   * 3. An empty object.
   *
   * @param {number|null} auditCriteriaId - The ID of the audit criteria to find
   *  the configuration for.
   *
   * @return {Object} - The calculation configuration to use as the base.
   */
  private findCalculationConfig(auditCriteriaId: number | null) {
    const configs = this.auditConfig.calculationConfigs || [];

    // Find the global configuration (where both criteria and site are null)
    const globalConfig = configs.find(
      (c: CalculationConfig) => !c.auditCriteriaMap && !c.auditSiteMap,
    );

    // Find a specific configuration for the given criterion
    const specificConfig = configs.find((c: CalculationConfig) => {
      const criteriaMapId = c.auditCriteriaMap ? c.auditCriteriaMap.id : null;
      // In single-site context, auditSiteMap will be null for relevant configs
      return auditCriteriaId === criteriaMapId && !c.auditSiteMap;
    });

    // Prioritize the specific config, fall back to global, then to an empty object
    const baseCalculation =
      (specificConfig && specificConfig.calculation) ||
      (globalConfig && globalConfig.calculation) ||
      {};

    // Merge the found configuration with the hardcoded default to ensure all fields are present
    const finalConfig = {
      ..._cloneDeep(this.oneOffCalculateData),
      ...baseCalculation,
    };

    // Add auditCriteriaId if it's for a specific row's calculator
    if (auditCriteriaId) {
      finalConfig.auditCriteriaId = auditCriteriaId;
    }

    return finalConfig;
  }

  public showSaveSuccessNotification() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  get auditCriteriaList() {
    return _orderBy(this.singleSitePayload, ['order'], ['asc']);
  }
}
